import { createProfileTW } from 'flow/actions/profile-actions';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey } from 'flow/types/EntityKeys';

import { homeActionUrlPath } from 'flow/utils';
import { licenseIsMobile } from 'flow/utils/flow-utils';
import { hasLandlineCallerId, PriorityItems } from 'flow/utils/profile-utils';
import { fromJS, Map } from 'immutable';
import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { ReferralNumberOption } from '../Setting/components/ReferralNumberCombobox';
import { SoundOption } from '../Setting/components/ReferralSoundCombobox';
import SettingSection from '../Setting/SettingSection';
import BaseModal from '../ui-components/BaseModal';
import Divider from '../ui-components/Divider';
import InputField from '../ui-components/InputField';
import RadioButton from '../ui-components/RadioButton';
import BusyNoAnswerSection from './CreateProfile/BusyNoAnswerSection';
import CallerCombobox from './CreateProfile/CallerCombobox';
import CallsNotLetThroughSection from './CreateProfile/CallsNotLetThroughSection';
import ConnectCallsToSection from './CreateProfile/ConnectCallsToSection';

type ReceiveCallsValues = 'all' | 'only_mobile' | 'none';
export type ConnectCallsValues = 'both' | 'fixed' | 'mobile';
export type BusyNoAnswerValues = 'voicemail' | 'busy';
export type CallsNotLetThroughValues =
    | 'voicemail'
    | 'referral_number'
    | 'hang_up'
    | 'repeat_sound';

interface Props {
    extensionKey?: ExtensionKey;
}

const setCallerId = (
    profileDTO: Map<string, unknown>,
    typeField: string,
    mainNumberField: string,
    callerId: string,
) =>
    ['fixed', 'msisdn', 'hidden'].includes(callerId)
        ? profileDTO.setIn(['callerId', typeField], callerId)
        : profileDTO
              .setIn(['callerId', typeField], 'mainanumber')
              .setIn(['callerId', mainNumberField, 'key'], callerId);

const CreateProfileModal = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extension } = useExtension(extensionKey);
    const { profiles } = useProfiles(extensionKey);

    const showLandline = hasLandlineCallerId(profiles.first());

    const hasMobileLicense = licenseIsMobile(
        extension.getIn(['license', 'licenseUsageType']),
    );

    const hasMobileExtension = useAppSelector((state) =>
        state.extensions.hasIn([
            'extensions',
            extensionKey,
            'mobileExtension',
            'number',
        ]),
    );

    const referralSounds = useAppSelector((state) =>
        state.profiles.get('referal_sounds'),
    );

    const [profileName, setProfileName] = useState('');
    const [receiveCalls, setReceiveCalls] = useState<ReceiveCallsValues>('all');
    const [connectCalls, setConnectCalls] =
        useState<ConnectCallsValues>('both');
    const [busyNoAnswer, setBusyNoAnswer] =
        useState<BusyNoAnswerValues>('voicemail');
    const [callsNotLetThrough, setCallsNotLetThrough] =
        useState<CallsNotLetThroughValues>('voicemail');
    const [shouldPlayReferralSound, setShouldPlayReferralSound] =
        useState(true);

    const getInitialReferralSound = () => {
        const sound = referralSounds.find(
            (sound) => sound.get('key') === 'referentry-3', // Unavailable
        );

        return {
            id: sound.get('key'),
            name: sound.get('description'),
            playbackUrl: sound.has('sound')
                ? homeActionUrlPath('/' + sound.getIn(['sound', 'playbackURL']))
                : null,
        };
    };

    const [referralSound, setReferralSound] = useState<SoundOption>(
        getInitialReferralSound(),
    );
    const [referralNumber, setReferralNumber] =
        useState<ReferralNumberOption>();
    const [callerFixed, setCallerFixed] = useState({
        id: 'fixed',
        name: i18next.t('settings.landline_number'),
    });
    const [callerMobile, setCallerMobile] = useState({
        id: 'msisdn',
        name: i18next.t('settings.mobile_number'),
    });

    const needsReferralNumber =
        ['only_mobile', 'none'].includes(receiveCalls) &&
        callsNotLetThrough === 'referral_number';

    const createProfile = () => {
        let profileDTO = fromJS({
            description: profileName,
            available: ['all'].includes(receiveCalls),
            defaultDuration: '0',
            priority: PriorityItems[50].key,
        });

        if (hasMobileExtension && ['all'].includes(receiveCalls)) {
            profileDTO = profileDTO.setIn(
                ['parallelCalls', 'parallelCallType'],
                showLandline ? connectCalls : 'mobile',
            );
        }

        if (hasMobileLicense) {
            const letThroughMexCalls = ['only_mobile'].includes(receiveCalls);
            profileDTO = profileDTO.setIn(
                ['parallelCalls', 'letThroughMexCalls'],
                letThroughMexCalls,
            );
            profileDTO = profileDTO.setIn(
                ['callForwarding', 'letThroughMexCalls'],
                letThroughMexCalls,
            );
        }

        if (['all', 'only_mobile'].includes(receiveCalls)) {
            profileDTO = profileDTO.setIn(
                ['callForwarding', 'busyRedirectNumber', 'type'],
                busyNoAnswer,
            );
            profileDTO = profileDTO.setIn(
                ['callForwarding', 'delayedRedirectNumber', 'type'],
                busyNoAnswer,
            );
        }

        if (['only_mobile', 'none'].includes(receiveCalls)) {
            switch (callsNotLetThrough) {
                case 'voicemail':
                    profileDTO = profileDTO.setIn(
                        ['callForwarding', 'unconditionalRedirect', 'type'],
                        'voicemail',
                    );
                    break;

                case 'referral_number':
                    profileDTO = profileDTO
                        .setIn(
                            ['callForwarding', 'unconditionalRedirect', 'type'],
                            'number',
                        )
                        .setIn(
                            [
                                'callForwarding',
                                'unconditionalRedirect',
                                'forwardingNumber',
                            ],
                            referralNumber.dto,
                        );
                    break;

                case 'hang_up':
                    profileDTO = profileDTO.setIn(
                        ['spokenNotification', 'referActionType'],
                        'hangup',
                    );
                    break;

                case 'repeat_sound':
                    profileDTO = profileDTO.setIn(
                        ['spokenNotification', 'referActionType'],
                        'loop',
                    );
                    break;
            }
        }

        if (
            shouldPlayReferralSound &&
            ['only_mobile', 'none'].includes(receiveCalls)
        ) {
            profileDTO = profileDTO.setIn(
                ['spokenNotification', 'referEntry', 'key'],
                referralSound.id,
            );
        }

        if (showLandline) {
            profileDTO = setCallerId(
                profileDTO,
                'fixedCallerIdType',
                'fixedMainNumber',
                callerFixed.id,
            );
        }

        if (hasMobileLicense) {
            profileDTO = setCallerId(
                profileDTO,
                'mexCallerIdType',
                'mexMainNumber',
                callerMobile.id,
            );
        }

        dispatch(createProfileTW(extensionKey, profileDTO));
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('settings.profile.create_new_profile')}
            disableSubmit={
                !profileName || (needsReferralNumber && !referralNumber?.dto)
            }
            onSubmit={createProfile}
            confirmButtonText={i18next.t('settings.profile.create_button')}
            className="w-[600px]"
        >
            <div className="flex flex-col px-2 pb-9">
                <SettingSection>
                    {i18next.t('settings.profile.wizard.name')}
                    <span>&nbsp;*</span>
                </SettingSection>

                <InputField
                    placeholder={i18next.t('settings.profile.name_field_hint')}
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                />

                <Divider />

                <SettingSection>
                    {i18next.t('settings.profile.wizard.incoming')}
                </SettingSection>

                <RadioButton
                    name="receiveCalls"
                    checked={receiveCalls === 'all'}
                    onChange={() => setReceiveCalls('all')}
                >
                    {i18next.t('settings.profile.wizard.incoming.all')}
                </RadioButton>

                {hasMobileLicense && (
                    <RadioButton
                        name="receiveCalls"
                        checked={receiveCalls === 'only_mobile'}
                        onChange={() => setReceiveCalls('only_mobile')}
                    >
                        {i18next.t('settings.profile.wizard.incoming.mobile')}
                    </RadioButton>
                )}

                <RadioButton
                    name="receiveCalls"
                    checked={receiveCalls === 'none'}
                    onChange={() => setReceiveCalls('none')}
                >
                    {i18next.t('settings.profile.wizard.incoming.none')}
                </RadioButton>

                {showLandline &&
                    hasMobileLicense &&
                    ['all'].includes(receiveCalls) && (
                        <ConnectCallsToSection
                            extensionKey={extensionKey}
                            selected={connectCalls}
                            onChange={setConnectCalls}
                        />
                    )}

                {['all', 'none'].includes(receiveCalls) && (
                    <BusyNoAnswerSection
                        selected={busyNoAnswer}
                        onChange={setBusyNoAnswer}
                    />
                )}

                {['only_mobile', 'none'].includes(receiveCalls) && (
                    <CallsNotLetThroughSection
                        enableReferralSound={shouldPlayReferralSound}
                        onToggleReferralSound={setShouldPlayReferralSound}
                        referralSound={referralSound}
                        onChangeReferralSound={setReferralSound}
                        referralNumber={referralNumber}
                        onChangeReferralNumber={setReferralNumber}
                        selected={callsNotLetThrough}
                        onChange={setCallsNotLetThrough}
                    />
                )}

                {(showLandline || hasMobileLicense) && (
                    <>
                        <Divider />

                        <SettingSection>
                            {i18next.t('settings.profile.wizard.callerid')}
                        </SettingSection>
                    </>
                )}

                {showLandline && (
                    <>
                        <SettingSection className="mt-2 text-sm/4">
                            {i18next.t(
                                'settings.profile.wizard.callerid.landline',
                            )}
                        </SettingSection>

                        <CallerCombobox
                            ariaLabel={i18next.t(
                                'settings.profile.wizard.callerid.landline',
                            )}
                            caller={callerFixed}
                            extensionKey={extensionKey}
                            onChange={setCallerFixed}
                        />
                    </>
                )}

                {hasMobileLicense && (
                    <>
                        <SettingSection className="mt-3 text-sm/4">
                            {i18next.t(
                                'settings.profile.wizard.callerid.mobile',
                            )}
                        </SettingSection>

                        <CallerCombobox
                            ariaLabel={i18next.t(
                                'settings.profile.wizard.callerid.mobile',
                            )}
                            caller={callerMobile}
                            extensionKey={extensionKey}
                            onChange={setCallerMobile}
                        />
                    </>
                )}

                <Divider />

                <span className="text-sm/4">
                    *&nbsp;
                    {i18next.t('fields.required_field')}
                </span>
            </div>
        </BaseModal>
    );
};

export default CreateProfileModal;
